import React, { useState } from 'react';
import { App as AntApp, Button, Card, Popconfirm, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

const { Paragraph } = Typography;

export interface StartOverDeps {
  /** Wipes the signed-in learner's data on the server (the `reset-account` function). */
  resetOnServer: () => Promise<void>;
  /** Empties this browser's copy of the data. */
  clearLocal: () => Promise<void>;
  /** Tells an installed extension to fetch the (now empty) account. */
  tellExtension: () => Promise<unknown>;
  reload: () => void;
}

/**
 * A card for the Settings tab of the staging and development website: "Start over as a new
 * user". It wipes the account's learning data so the first-run flow (language, first deck) can
 * be tested again with the same sign-in. The production website never shows it, and the server
 * refuses the request there.
 */
export function createStartOver(deps: StartOverDeps): React.FC {
  return function StartOver() {
    const { message } = AntApp.useApp();
    const [busy, setBusy] = useState(false);

    const startOver = async () => {
      setBusy(true);
      try {
        await deps.resetOnServer();
        await deps.clearLocal();
        await deps.tellExtension().catch(() => undefined);
        deps.reload();
      } catch (err) {
        console.error('[LingoPing] Could not start over:', err);
        message.error("Couldn't start over. Nothing was deleted on this device.");
        setBusy(false);
      }
    };

    return (
      <Card title="Testing (staging only)">
        <Paragraph type="secondary">
          Deletes this account&apos;s cards, decks, known words and history, here and in the
          extension, so you see LingoPing as a new user does.
        </Paragraph>
        <Popconfirm
          title="Start over as a new user?"
          description="Everything this staging account has learned is deleted."
          okText="Delete and start over"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
          onConfirm={startOver}
        >
          <Button danger icon={<ReloadOutlined />} loading={busy}>
            Start over as a new user
          </Button>
        </Popconfirm>
      </Card>
    );
  };
}
